import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Sparkles, ArrowRight, Heart, Share2, ShoppingCart,
  Briefcase, Coffee, PartyPopper, Plane, Gift,
  Shield, Lock, Eye, MessageCircle, Instagram, 
  Smartphone, Image as ImageIcon, ChevronRight, Users
} from 'lucide-react';
import MarketingLayout from '../../components/MarketingLayout';
import SEO, { SEO_CONFIGS } from '../../components/SEO';
import { MOMENTS_PAGE_CONFIG } from '../../config/marketingConfig';

const MomentsPage = ({ config = MOMENTS_PAGE_CONFIG }) => {
  const [selectedMoment, setSelectedMoment] = useState('creative_workday');
  const [isInView, setIsInView] = useState(false);
  const galleryRef = useRef(null);

  // Intersection observer for gallery section
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsInView(entry.isIntersecting),
      { threshold: 0.3 }
    );
    if (galleryRef.current) observer.observe(galleryRef.current);
    return () => observer.disconnect();
  }, []);

  const momentIcons = {
    creative_workday: Briefcase,
    unwind: Coffee,
    after_hours: PartyPopper,
    the_escape: Plane,
    gentle_celebration: Gift
  };

  return (
    <MarketingLayout>
      <SEO {...SEO_CONFIGS.moments} />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-rose-50 via-white to-purple-50 pt-20 pb-16 sm:pt-28 sm:pb-24">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-rose-100 rounded-full blur-3xl opacity-50" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-100 rounded-full blur-3xl opacity-50" />
        </div>
        
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-2 leading-tight">
              {config.hero.headline}
              <span className="block text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-rose-600 to-purple-600 bg-clip-text text-transparent leading-tight">
                {config.hero.headlineAccent}
              </span>
            </h1>
            
            <p className="text-lg sm:text-xl text-slate-600 mb-8 max-w-2xl mx-auto mt-6">
              {config.hero.subheadline}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Moments Gallery Demo */}
      <section ref={galleryRef} className="py-16 sm:py-24 bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">
              See Moments in Action
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Select a moment to see how the same try-on transforms into different lifestyle scenes
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-8 items-start">
            {/* Base Try-On Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-1"
            >
              <p className="text-slate-400 text-sm mb-3 text-center lg:text-left">Try-On Result</p>
              <div className="relative rounded-2xl overflow-hidden bg-slate-700 aspect-[3/4] max-w-xs mx-auto lg:mx-0">
                <img
                  src={config.baseImage}
                  alt="Try-On Base"
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <span className="px-3 py-1.5 bg-white/20 backdrop-blur rounded-full text-white text-sm">
                    Base Image
                  </span>
                </div>
              </div>
            </motion.div>

            {/* Moments Preview */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-2"
            >
              <p className="text-slate-400 text-sm mb-3 text-center lg:text-left">Selected Moment</p>
              
              {/* Large Preview */}
              <div className="relative rounded-2xl overflow-hidden bg-slate-700 aspect-[4/5] sm:aspect-[3/4] mb-6">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={selectedMoment}
                    src={config.moments[selectedMoment].image}
                    alt={config.moments[selectedMoment].label}
                    loading="lazy"
                    initial={{ opacity: 0, scale: 1.05 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.5 }}
                    className="w-full h-full object-cover"
                  />
                </AnimatePresence>
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                
                {/* Moment Label */}
                <div className="absolute bottom-4 left-4 right-4">
                  <motion.div
                    key={selectedMoment}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center justify-between"
                  >
                    <div>
                      <p className="text-white font-semibold text-lg">
                        {config.moments[selectedMoment].label}
                      </p>
                      <p className="text-white/70 text-sm">
                        {config.moments[selectedMoment].description}
                      </p>
                    </div>
                    <span className="px-3 py-1.5 bg-white/20 backdrop-blur rounded-full text-white text-xs">
                      Moments by NanoKart.ai
                    </span>
                  </motion.div>
                </div>
              </div>

              {/* Moment Selector */}
              <div className="flex flex-wrap justify-center lg:justify-start gap-2">
                {Object.entries(config.moments).map(([key, moment]) => {
                  const Icon = momentIcons[key];
                  const isSelected = selectedMoment === key;
                  return (
                    <button
                      key={key}
                      onClick={() => setSelectedMoment(key)}
                      className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                        isSelected
                          ? 'bg-gradient-to-r from-rose-500 to-purple-600 text-white shadow-lg'
                          : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {moment.label}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Moments Increase Conversions */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              From Fit Confidence to Purchase Confidence
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Eye, title: "Visualize Real-Life", desc: "Helps customers visualize real-life usage" },
              { icon: Heart, title: "Remove Doubt", desc: '"Will I ever wear this?" doubt eliminated' },
              { icon: Share2, title: "Easy Sharing", desc: "Encourages sharing with friends/family" },
              { icon: ShoppingCart, title: "Emotional Connection", desc: "Creates emotional attachment to the outfit" }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-gradient-to-br from-rose-50 to-purple-50 rounded-2xl p-6 hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-4 shadow-sm">
                  <item.icon className="w-6 h-6 text-rose-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Where Partners Use Moments */}
      <section className="py-16 sm:py-24 bg-gradient-to-br from-slate-50 to-rose-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Where Partners Use Moments
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {[
              { icon: ImageIcon, label: "Product Pages" },
              { icon: MessageCircle, label: "WhatsApp Selling" },
              { icon: Instagram, label: "Instagram Posts" },
              { icon: Smartphone, label: "Ads & Banners" },
              { icon: Gift, label: "Festival Campaigns" }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                className="bg-white rounded-2xl p-5 text-center shadow-sm hover:shadow-md transition-all group"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-rose-100 to-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform">
                  <item.icon className="w-7 h-7 text-rose-600" />
                </div>
                <p className="text-slate-700 font-medium text-sm">{item.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust & Transparency */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-sm font-medium mb-4">
                <Shield className="w-4 h-4" />
                Privacy & Trust
              </span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Built with Privacy & Trust
              </h2>
              
              <div className="space-y-4">
                {[
                  { icon: Lock, text: "Customer photos are processed securely" },
                  { icon: Shield, text: "Images are not used for training" },
                  { icon: Eye, text: "Outputs are watermarked by default" },
                  { icon: Users, text: "Partners control usage & sharing" }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                      <item.icon className="w-5 h-5 text-green-600" />
                    </div>
                    <span className="text-slate-700">{item.text}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center">
                    <Shield className="w-8 h-8 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">100%</p>
                    <p className="text-slate-600">Secure Processing</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full" />
                    <span className="text-slate-600 text-sm">End-to-end encryption</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full" />
                    <span className="text-slate-600 text-sm">GDPR compliant</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full" />
                    <span className="text-slate-600 text-sm">No data retention</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 sm:py-24 bg-gradient-to-r from-rose-500 to-purple-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Heart className="w-12 h-12 text-white/80 mx-auto mb-6" />
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-6">
              Turn Try-Ons into Stories That Sell
            </h2>
            <p className="text-white/80 mb-8 max-w-xl mx-auto">
              Help your customers see themselves in the moment, and watch conversions grow.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/partners/register"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-rose-600 font-semibold rounded-xl hover:bg-rose-50 transition-all"
              >
                Start Free
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 text-white font-semibold rounded-xl border border-white/20 hover:bg-white/20 transition-all"
              >
                Talk to Sales
                <ChevronRight className="w-5 h-5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </MarketingLayout>
  );
};

export default MomentsPage;
